import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { Feature, FeatureCollection, Position } from 'geojson';

import type { PoiPipelinesResponse } from '@/api/poiPipelinesApi';
import { addOrbitLayer, ORBIT_LAYER_IDS, type MapLayerGroup } from '@/components/map/utils/mapLayerOrder';
import { circlePolygon } from '@/utils/geodesy';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';

export const POI_RADIUS_SOURCE_ID = 'orbit-poi-radius-source';

// Map layer paints cannot reference CSS variables, so the Origin Blue token is hardcoded
// here like every other layer.
const ORIGIN_BLUE = '#007CFF';

// The spotlight mask's wash colour. White reads as "washed out" over the light basemap and
// as haze over satellite imagery — both convey "outside the search area".
const MASK_COLOUR = '#FFFFFF';

const MASK_FILTER: maplibregl.FilterSpecification = ['==', ['get', 'part'], 'mask'];
const CIRCLE_FILTER: maplibregl.FilterSpecification = ['==', ['get', 'part'], 'circle'];
const BOUNDARY_FILTER: maplibregl.FilterSpecification = ['==', ['get', 'part'], 'boundary'];
const SEGMENT_FILTER: maplibregl.FilterSpecification = ['==', ['get', 'part'], 'segment'];

// The dashed boundary ring floats just outside the disc with a visible gap between them,
// so it is drawn on its own slightly larger circle. Proportional (not fixed-metre) so the
// gap keeps the same look at every radius.
const BOUNDARY_RADIUS_FACTOR = 1.03;

// A world-sized exterior ring for the spotlight mask, so it never needs regenerating as
// the user pans; the circle is punched out of it as an interior ring.
const WORLD_RING: Position[] = [
    [-180, -85],
    [180, -85],
    [180, 85],
    [-180, 85],
    [-180, -85],
];

/**
 * One shared source: the dimming mask (world polygon with a circle-shaped hole), the
 * search circle itself, and the server-clipped pipe sections, tagged by `part`.
 *
 * The circle is drawn from the response's calculation centre — never from the clicked
 * feature's rendered coordinates, which for a leak entity would be the leak location
 * rather than the point the lengths were calculated around.
 */
function buildRadiusFeatureCollection(data: PoiPipelinesResponse | null): FeatureCollection {
    if (!data) return EMPTY_FEATURE_COLLECTION;

    const centre: [number, number] = [data.centre.longitude, data.centre.latitude];
    const circle = circlePolygon(centre, data.radiusMetres);
    const boundary = circlePolygon(centre, data.radiusMetres * BOUNDARY_RADIUS_FACTOR);

    const features: Feature[] = [
        {
            type: 'Feature',
            properties: { part: 'mask' },
            geometry: { type: 'Polygon', coordinates: [WORLD_RING, circle.coordinates[0]] },
        },
        { type: 'Feature', properties: { part: 'circle' }, geometry: circle },
        { type: 'Feature', properties: { part: 'boundary' }, geometry: boundary },
        ...data.features.features.map(
            (feature): Feature => ({
                ...feature,
                properties: { ...feature.properties, part: 'segment' },
            }),
        ),
    ];

    return { type: 'FeatureCollection', features };
}

function registerRadiusSourceAndLayers(mlMap: maplibregl.Map, data: FeatureCollection): void {
    try {
        if (mlMap.getSource(POI_RADIUS_SOURCE_ID)) {
            (mlMap.getSource(POI_RADIUS_SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
        } else {
            mlMap.addSource(POI_RADIUS_SOURCE_ID, { type: 'geojson', data });
        }
    } catch {
        return;
    }

    // The mask alone renders in the spotlight group above the markers, so features outside
    // the circle are washed out. Everything else — the circle, its rim decorations and the
    // clipped sections — renders in the selectionRadius group just above the base
    // pipelines, so fittings, clusters and markers inside the circle stay fully visible on
    // top of the highlight.
    const layers: [maplibregl.LayerSpecification, MapLayerGroup][] = [
        [
            {
                // Dims the basemap and every Orbit overlay rendered below this group outside the
                // circle; inside the hole everything keeps its normal styling. The selected POI
                // marker, its halo and the clipped sections render above the mask.
                id: ORBIT_LAYER_IDS.poiRadiusMask,
                type: 'fill',
                source: POI_RADIUS_SOURCE_ID,
                filter: MASK_FILTER,
                // 70% wash per the design — only ~30% of the map shows through outside the
                // circle, keeping the focus firmly on the selected POI.
                paint: { 'fill-color': MASK_COLOUR, 'fill-opacity': 0.7 },
            },
            'selectionSpotlight',
        ],
        [
            {
                id: ORBIT_LAYER_IDS.poiRadiusFill,
                type: 'fill',
                source: POI_RADIUS_SOURCE_ID,
                filter: CIRCLE_FILTER,
                paint: { 'fill-color': ORIGIN_BLUE, 'fill-opacity': 0.1 },
            },
            'selectionRadius',
        ],
        [
            {
                // MapLibre fills cannot paint radial gradients, so the design's inner shadow
                // (transparent until just short of the rim, ~10% black at it) is approximated by
                // a wide blurred dark line offset to hug the inside of the disc edge.
                id: ORBIT_LAYER_IDS.poiRadiusInnerShadow,
                type: 'line',
                source: POI_RADIUS_SOURCE_ID,
                filter: CIRCLE_FILTER,
                paint: {
                    'line-color': '#000000',
                    'line-width': 7,
                    'line-blur': 5,
                    'line-opacity': 0.12,
                    'line-offset': 3,
                },
            },
            'selectionRadius',
        ],
        [
            {
                // Solid grey rim on the disc edge — approximates the design's inner ring and its
                // subtle inward shadow without any custom rendering.
                id: ORBIT_LAYER_IDS.poiRadiusInnerRing,
                type: 'line',
                source: POI_RADIUS_SOURCE_ID,
                filter: CIRCLE_FILTER,
                paint: { 'line-color': '#9CA3AF', 'line-width': 2.5, 'line-blur': 1, 'line-opacity': 0.9 },
            },
            'selectionRadius',
        ],
        [
            {
                // The dashed ring floats on its own larger circle, leaving a clear gap to the
                // grey-rimmed disc edge.
                id: ORBIT_LAYER_IDS.poiRadiusOutline,
                type: 'line',
                source: POI_RADIUS_SOURCE_ID,
                filter: BOUNDARY_FILTER,
                // Dasharray is in line-width multiples: [4, 4] at 2px = the design's 8px dashes
                // with 8px gaps.
                paint: { 'line-color': ORIGIN_BLUE, 'line-width': 2, 'line-dasharray': [4, 4] },
            },
            'selectionRadius',
        ],
        [
            {
                // No minzoom and no semantic-zoom entry: the sections belong to an explicit
                // selection, so they stay visible even at zooms where the normal pipeline layers
                // are hidden (POIs are selectable from well below the pipelines band).
                id: ORBIT_LAYER_IDS.poiRadiusPipelines,
                type: 'line',
                source: POI_RADIUS_SOURCE_ID,
                filter: SEGMENT_FILTER,
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: { 'line-color': ORIGIN_BLUE, 'line-width': 4 },
            },
            'selectionRadius',
        ],
    ];

    for (const [layer, group] of layers) {
        if (!mlMap.getLayer(layer.id)) {
            try {
                addOrbitLayer(mlMap, layer, group);
            } catch {
                // Progressive enhancement — layer failures must not break the map.
            }
        }
    }
}

/**
 * Draws the selected POI's search radius: a spotlight mask dimming everything outside the
 * circle, the circle itself (dashed boundary, soft inner ring, translucent fill) and the
 * clipped in-radius pipe sections returned by the API.
 *
 * The section geometry comes straight from the API (PostGIS does the clipping) — this hook
 * only pushes it into a dedicated source and never touches the normal pipeline layers.
 *
 * Passing `null` clears the display, so the caller removes it by dropping the data —
 * deselecting, switching POI, activating a tool or switching client all clear the
 * selection upstream and the radius follows.
 */
export function usePoiRadiusLayer(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    data: PoiPipelinesResponse | null,
): void {
    // Held in a ref so the style.load handler can re-register with the current data without
    // re-running the registration effect on every selection change.
    const dataRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);
    useLayoutEffect(() => {
        dataRef.current = buildRadiusFeatureCollection(data);
    });

    // Push new data (or clear) into the source.
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        setGeoJsonSourceData(mlMap, POI_RADIUS_SOURCE_ID, buildRadiusFeatureCollection(data));
    }, [data, isMapLoaded, mapRef]);

    // Register source and layers once the map is ready, and again after every base map style
    // switch (which drops all sources and layers).
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const register = () => {
            registerRadiusSourceAndLayers(mlMap, dataRef.current);
        };

        register();
        mlMap.on('style.load', register);

        return () => {
            mlMap.off('style.load', register);
        };
    }, [isMapLoaded, mapRef]);
}
